/**
 * Approvals (/dashboard/admin): Chicago Politics staff only (the platform team with a staff-domain email; enforced by the
 * Worker, dashboard-staff.js). Not designed: built from the Access screen's parts (page head, invite popover, members
 * table, chips). Pending office / advocacy / resident requests, oldest first; Approve or Deny shrinks and fades the row
 * (O5 widgetRemove), the gap closes, and an Undo toast holds the decision for 5 s before it's sent. Recent decisions keep
 * who decided and when. Staff owners/admins decide and invite; staff members view. Paints from snapshot:admin.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ChevronDown, UserPlus } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { initialsOf, lastActiveLabel } from "../account/logic";
import { prefersReducedMotion, recipes, reducedFallback } from "../motion";
import { byOldest, GROUP_LABEL, isStaffEmail, requestTitle } from "./logic";
import type { ApprovalDecision, ApprovalRequest, ApprovalsSnapshot } from "./types";
import { Head } from "./Organizations";
import { useBusy } from "../hooks/useBusy";

type Decision = "approve" | "deny";

async function call(path: string, method: string, body?: unknown) {
  const res = await fetch(path, { method, credentials: "include", headers: body ? { "content-type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
  const data = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) toastStore.getState().show({ message: errorText(data) ?? "Couldn't save that. Try again." });
  return res?.ok ? data : null;
}

const decisionPath = (r: ApprovalRequest) => `/api/dashboard/admin/approvals/${r.kind === "resident" ? "resident" : "team"}/${encodeURIComponent(r.userId)}`;
const fullDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : undefined);

function Who({ name, email }: { name: string; email: string }) {
  return (
    <RowMain min={130}>
      <span className="members__who" role="cell">
        <span className="members__avatar" aria-hidden>{initialsOf(name || email.split("@")[0]!.replace(/[._-]+/g, " "))}</span>
        <span><RowTitle as="b">{name || email}</RowTitle><RowSub>{email}</RowSub></span>
      </span>
    </RowMain>
  );
}

function Org({ r }: { r: Pick<ApprovalRequest, "kind" | "orgName" | "ward" | "mode"> }) {
  return (
    <RowMain min={120}>
      <span className="approvals__org" role="cell">
        <RowTitle as="b">{requestTitle(r)}</RowTitle>
        <RowSub>{GROUP_LABEL[r.kind]}{r.mode ? ` · ${r.mode === "join" ? "Join team" : "New team"}` : ""}</RowSub>
      </span>
    </RowMain>
  );
}


export function Approvals({ initial }: { initial: ApprovalsSnapshot }) {
  const [requests, setRequests] = useState(initial.requests);
  const [recent, setRecent] = useState(initial.recent);
  const [leaving, setLeaving] = useState<Record<string, "fade" | "collapse">>({});
  const [restored, setRestored] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  // Decisions waiting out their Undo toast: sent when the toast closes, or right away if the page is left.
  const held = useRef(new Map<string, { path: string; decision: Decision; send: () => void }>());
  const now = new Date();

  useEffect(() => { document.title = "Approvals · Chicago Politics"; }, []);
  useEffect(() => {
    const flush = () => {
      for (const h of held.current.values()) {
        void fetch(h.path, { method: "POST", credentials: "include", keepalive: true, headers: { "content-type": "application/json" }, body: JSON.stringify({ decision: h.decision }) });
      }
      held.current.clear();
    };
    addEventListener("pagehide", flush);
    return () => removeEventListener("pagehide", flush);
  }, []);

  const restore = (r: ApprovalRequest) => {
    setRequests((rs) => (rs.some((x) => x.id === r.id) ? rs : [...rs, r].sort(byOldest)));
    setRestored(r.id);
  };

  const decide = (r: ApprovalRequest, decision: Decision) => {
    if (leaving[r.id]) return;
    const reduced = prefersReducedMotion();
    const fade = reduced ? reducedFallback.duration : recipes.widgetRemove.duration;
    const collapse = reduced ? 0 : recipes.widgetRemove.collapse;
    setLeaving((l) => ({ ...l, [r.id]: "fade" }));
    setTimeout(() => setLeaving((l) => ({ ...l, [r.id]: "collapse" })), fade);
    setTimeout(() => {
      setRequests((rs) => rs.filter((x) => x.id !== r.id));
      setLeaving(({ [r.id]: _, ...l }) => l);
      let settled = false;
      const send = async () => {
        if (settled) return;
        settled = true;
        held.current.delete(r.id);
        const res = await call(decisionPath(r), "POST", { decision }) as { decision?: ApprovalDecision } | null;
        if (res?.decision) setRecent((d) => [res.decision!, ...d.filter((x) => x.id !== res.decision!.id)].slice(0, 25));
        else restore(r);
      };
      held.current.set(r.id, { path: decisionPath(r), decision, send });
      toastStore.getState().show({
        message: `${decision === "approve" ? "Approved" : "Denied"} ${requestTitle(r)}`, actionLabel: "Undo",
        onAction: () => { settled = true; held.current.delete(r.id); restore(r); },
        onClose: () => void send(),
      });
    }, fade + collapse);
  };

  return (
    <div className="access-page approvals-page">
      <header className="access-head">
        <h1 className="page-title">Approvals</h1>
        {initial.canDecide && (
          <div className="access-invite">
            <button className="btn btn--primary btn--lg" aria-expanded={inviting} onClick={() => setInviting((v) => !v)}><UserPlus size={16} />Invite</button>
            {inviting && <StaffInvite domain={initial.staffDomain} onDone={() => setInviting(false)} />}
          </div>
        )}
      </header>

      {requests.length === 0 ? <p className="empty-panel">No pending requests.</p> : (
        <List as="div" className="members approvals" role="table" aria-label="Pending requests">
          <ListRow as="div" className="members__head ls-head" role="row">
            <Head min={120}>Organization</Head><Head min={130}>Requested by</Head><Head min={100}>City email</Head>
            <Head width={96}>Asked</Head><Head width={176} />
          </ListRow>
          {requests.map((r) => (
            <ListRow key={r.id} as="div" className={`members__row approvals__row${leaving[r.id] ? ` is-${leaving[r.id] === "fade" ? "removing" : "collapsing"}` : ""}`} role="row"
              data-restored={restored === r.id || undefined} aria-hidden={leaving[r.id] ? true : undefined}>
              <Org r={r} />
              <Who name={r.name} email={r.email} />
              <RowMain min={100}>
                <span className="approvals__city" role="cell">
                  {r.cityEmail && <span className={`chip ${r.cityEmail === "verified" ? "chip--positive" : "chip--warning"}`}>{r.cityEmail === "verified" ? "Verified" : "Unverified"}</span>}
                  {r.workEmail && r.workEmail !== r.email && <small>{r.workEmail}</small>}
                </span>
              </RowMain>
              <RowCell width={96}><span role="cell" className="members__active" title={fullDate(r.requestedAt)}>{lastActiveLabel(r.requestedAt, now)}</span></RowCell>
              <RowCell width={176} align="end" className="approvals__actions-cell">
                <span role="cell" className="approvals__actions">
                  {initial.canDecide && <>
                    <button className="btn btn--primary btn--small" onClick={() => decide(r, "approve")}>Approve</button>
                    <button className="btn btn--secondary btn--small" onClick={() => decide(r, "deny")}>Deny</button>
                  </>}
                </span>
              </RowCell>
            </ListRow>
          ))}
        </List>
      )}

      {recent.length > 0 && (
        <section className="approvals-recent" aria-labelledby="approvals-recent">
          <h2 className="section-title" id="approvals-recent">Recent decisions</h2>
          <List as="div" className="members approvals approvals--recent" role="table" aria-labelledby="approvals-recent">
            <ListRow as="div" className="members__head ls-head" role="row">
              <Head min={120}>Organization</Head><Head min={130}>Requested by</Head><Head width={110}>Decision</Head>
              <Head min={100}>By</Head><Head width={96}>When</Head>
            </ListRow>
            {recent.map((d) => (
              <ListRow key={d.id} as="div" className="members__row approvals__row" role="row">
                <Org r={d} />
                <Who name={d.name} email={d.email} />
                <RowCell width={110}><span role="cell"><span className={`chip ${d.decision === "approved" ? "chip--positive" : "chip--negative"}`}>{d.decision === "approved" ? "Approved" : "Denied"}</span></span></RowCell>
                <RowMain min={100}><span role="cell" className="members__active">{d.decidedBy ?? ""}</span></RowMain>
                <RowCell width={96}><span role="cell" className="members__active" title={fullDate(d.decidedAt)}>{lastActiveLabel(d.decidedAt, now)}</span></RowCell>
              </ListRow>
            ))}
          </List>
        </section>
      )}
    </div>
  );
}

/** Invite staff: an @<staff domain> email + role → POST /api/dashboard/admin/staff/invite (the designed invite email). */
function StaffInvite({ domain, onDone }: { domain: string; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const wrong = email.includes("@") && email.trim().length > 3 && !isStaffEmail(email, domain);
  const { busy, run } = useBusy();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!isStaffEmail(email, domain)) { toastStore.getState().show({ message: `Use an @${domain} email.` }); return; }
    void run(async () => {
      if (await call("/api/dashboard/admin/staff/invite", "POST", { email: email.trim(), role })) {
        toastStore.getState().show({ message: `Invite sent to ${email.trim().toLowerCase()}` });
        onDone();
      }
    });
  };
  return (
    <form className="invite-form" onSubmit={submit}>
      <input ref={input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" aria-invalid={wrong || undefined}
        placeholder={`name@${domain}`} />
      <Dropdown className="role-select" label="Role" value={role} onChange={setRole} chevron={<ChevronDown size={14} aria-hidden />}
        options={[{ value: "member" as const, label: "Member" }, { value: "admin" as const, label: "Admin" }]} />
      <button className="btn btn--primary" type="submit" disabled={busy} aria-busy={busy || undefined}><UserPlus size={14} />Invite</button>
    </form>
  );
}
